function typeAt(view, off) {
  return String.fromCharCode(
    view.getUint8(off + 4),
    view.getUint8(off + 5),
    view.getUint8(off + 6),
    view.getUint8(off + 7)
  );
}

function readBoxes(view, start, end) {
  const boxes = [];
  let off = start;
  const length = view.byteLength;
  const limit = Math.min(end, length);
  while (off + 8 <= limit) {
    let size = view.getUint32(off);
    const type = typeAt(view, off);
    let hdr = 8;
    if (size === 1) {
      if (off + 16 > limit) break;
      size = Number(view.getBigUint64(off + 8));
      hdr = 16;
    }
    if (size === 0) size = limit - off;
    if (size < hdr || off + size > length) break;
    boxes.push({ type, off, size, hdr, content: off + hdr, end: off + size });
    off += size;
  }
  return boxes;
}

function findBox(boxes, type) {
  return boxes.find((box) => box.type === type);
}

function handlerType(view, hdlr) {
  return String.fromCharCode(
    view.getUint8(hdlr.content + 8),
    view.getUint8(hdlr.content + 9),
    view.getUint8(hdlr.content + 10),
    view.getUint8(hdlr.content + 11)
  );
}

function readStts(view, box) {
  const count = view.getUint32(box.content + 4);
  const entries = [];
  let off = box.content + 8;
  for (let i = 0; i < count; i += 1) {
    entries.push({
      sampleCount: view.getUint32(off),
      duration: view.getUint32(off + 4),
    });
    off += 8;
  }
  return entries;
}

function readCtts(view, box) {
  if (!box) return null;
  const version = view.getUint8(box.content);
  const count = view.getUint32(box.content + 4);
  const entries = [];
  let off = box.content + 8;
  for (let i = 0; i < count; i += 1) {
    const sampleCount = view.getUint32(off);
    const offset = version === 1 ? view.getInt32(off + 4) : view.getUint32(off + 4);
    entries.push({ sampleCount, offset });
    off += 8;
  }
  return entries;
}

function readStss(view, box, sampleCount) {
  const sync = new Array(sampleCount).fill(false);
  if (!box) {
    sync[0] = true;
    return sync;
  }
  const count = view.getUint32(box.content + 4);
  let off = box.content + 8;
  for (let i = 0; i < count; i += 1) {
    const index = view.getUint32(off) - 1;
    if (index >= 0 && index < sampleCount) sync[index] = true;
    off += 4;
  }
  return sync;
}

function readStsz(view, box) {
  const uniform = view.getUint32(box.content + 4);
  const count = view.getUint32(box.content + 8);
  if (uniform) return new Array(count).fill(uniform);
  const sizes = new Array(count);
  let off = box.content + 12;
  for (let i = 0; i < count; i += 1) {
    sizes[i] = view.getUint32(off);
    off += 4;
  }
  return sizes;
}

function readStsc(view, box) {
  const count = view.getUint32(box.content + 4);
  const entries = [];
  let off = box.content + 8;
  for (let i = 0; i < count; i += 1) {
    entries.push({
      firstChunk: view.getUint32(off),
      samplesPerChunk: view.getUint32(off + 4),
    });
    off += 12;
  }
  return entries;
}

function readOffsets(view, box) {
  const count = view.getUint32(box.content + 4);
  const offsets = new Array(count);
  let off = box.content + 8;
  if (box.type === 'co64') {
    for (let i = 0; i < count; i += 1) {
      offsets[i] = Number(view.getBigUint64(off));
      off += 8;
    }
  } else {
    for (let i = 0; i < count; i += 1) {
      offsets[i] = view.getUint32(off);
      off += 4;
    }
  }
  return offsets;
}

function sampleOffsets(stsc, chunkOffsets, sizes) {
  const offsets = new Array(sizes.length);
  let sample = 0;
  let entry = 0;
  for (let chunk = 1; chunk <= chunkOffsets.length && sample < sizes.length; chunk += 1) {
    if (entry + 1 < stsc.length && chunk >= stsc[entry + 1].firstChunk) entry += 1;
    const perChunk = stsc[entry].samplesPerChunk;
    let pos = chunkOffsets[chunk - 1];
    for (let i = 0; i < perChunk && sample < sizes.length; i += 1) {
      offsets[sample] = pos;
      pos += sizes[sample];
      sample += 1;
    }
  }
  return offsets;
}

function expandTiming(stts, ctts, sampleCount) {
  const dts = new Array(sampleCount);
  const duration = new Array(sampleCount);
  let time = 0;
  let index = 0;
  stts.forEach((entry) => {
    for (let i = 0; i < entry.sampleCount && index < sampleCount; i += 1) {
      dts[index] = time;
      duration[index] = entry.duration;
      time += entry.duration;
      index += 1;
    }
  });
  const composition = new Array(sampleCount).fill(0);
  if (ctts) {
    index = 0;
    ctts.forEach((entry) => {
      for (let i = 0; i < entry.sampleCount && index < sampleCount; i += 1) {
        composition[index] = entry.offset;
        index += 1;
      }
    });
  }
  return { dts, duration, composition };
}

export function parseMp4Video(buffer) {
  const view = new DataView(buffer);
  const top = readBoxes(view, 0, buffer.byteLength);
  const moov = findBox(top, 'moov');
  if (!moov) throw new Error('moov missing');
  const moovKids = readBoxes(view, moov.content, moov.end);

  for (let t = 0; t < moovKids.length; t += 1) {
    const trak = moovKids[t];
    if (trak.type !== 'trak') continue;
    const trakKids = readBoxes(view, trak.content, trak.end);
    const mdia = findBox(trakKids, 'mdia');
    if (!mdia) continue;
    const mdiaKids = readBoxes(view, mdia.content, mdia.end);
    const hdlr = findBox(mdiaKids, 'hdlr');
    if (!hdlr || handlerType(view, hdlr) !== 'vide') continue;

    const mdhd = findBox(mdiaKids, 'mdhd');
    const mdhdVer = view.getUint8(mdhd.content);
    const timescale = mdhdVer === 1
      ? view.getUint32(mdhd.content + 20)
      : view.getUint32(mdhd.content + 12);
    const minf = findBox(mdiaKids, 'minf');
    const minfKids = readBoxes(view, minf.content, minf.end);
    const stbl = findBox(minfKids, 'stbl');
    const stblKids = readBoxes(view, stbl.content, stbl.end);
    const stsd = findBox(stblKids, 'stsd');
    const entries = readBoxes(view, stsd.content + 8, stsd.end);
    const avc1 = entries.find((box) => box.type === 'avc1' || box.type === 'avc3');
    if (!avc1) throw new Error('avc1 missing');

    const width = view.getUint16(avc1.content + 24);
    const height = view.getUint16(avc1.content + 26);
    const avcKids = readBoxes(view, avc1.content + 78, avc1.end);
    const avcC = findBox(avcKids, 'avcC');
    if (!avcC) throw new Error('avcC missing');
    const description = new Uint8Array(buffer.slice(avcC.content, avcC.end));
    const profile = description[1];
    const compat = description[2];
    const level = description[3];
    const codec = `avc1.${[profile, compat, level].map((n) => n.toString(16).padStart(2, '0')).join('')}`;

    const sizes = readStsz(view, findBox(stblKids, 'stsz'));
    const stsc = readStsc(view, findBox(stblKids, 'stsc'));
    const chunkOffsets = readOffsets(view, findBox(stblKids, 'co64') || findBox(stblKids, 'stco'));
    const offsets = sampleOffsets(stsc, chunkOffsets, sizes);
    const { dts, duration, composition } = expandTiming(
      readStts(view, findBox(stblKids, 'stts')),
      readCtts(view, findBox(stblKids, 'ctts')),
      sizes.length
    );
    const sync = readStss(view, findBox(stblKids, 'stss'), sizes.length);
    const samples = sizes.map((size, i) => ({
      offset: offsets[i],
      size,
      dts: Math.round((dts[i] * 1e6) / timescale),
      timestamp: Math.round(((dts[i] + composition[i]) * 1e6) / timescale),
      duration: Math.round((duration[i] * 1e6) / timescale),
      type: sync[i] ? 'key' : 'delta',
    }));

    return { codec, description, width, height, samples };
  }

  throw new Error('video track missing');
}

export function canPlayMp4OnCanvas() {
  return typeof window !== 'undefined'
    && typeof window.VideoDecoder === 'function'
    && typeof window.EncodedVideoChunk === 'function';
}

export async function playMp4OnCanvas(canvas, url, options) {
  const signal = options && options.signal;
  const onReady = options && options.onReady;
  const onError = options && options.onError;
  if (!canPlayMp4OnCanvas()) return false;

  const Decoder = window.VideoDecoder;
  const Chunk = window.EncodedVideoChunk;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return false;

  let response;
  const early = window.__spgHeroVideoPromise;
  if (early && String(url).indexOf('/videos/hero-drone.mp4') !== -1) {
    window.__spgHeroVideoPromise = null;
    response = await early;
  } else {
    response = await fetch(url, { signal });
  }
  if (!response || !response.ok || !response.body) throw new Error('video fetch failed');

  const reader = response.body.getReader();
  const contentLength = Number(response.headers.get('Content-Length')) || 0;
  let bytes = contentLength ? new Uint8Array(contentLength) : new Uint8Array(512 * 1024);
  let received = 0;
  let parsed = null;
  let decoder = null;
  let stopped = false;
  let pending = null;
  let index = 0;
  let origin = 0;
  let stalledAt = 0;
  let started = false;
  let looping = false;
  let loaded = false;

  const fitCanvas = () => {
    const parent = canvas.parentElement;
    const w = (parent && parent.clientWidth) || canvas.clientWidth || window.innerWidth;
    const h = (parent && parent.clientHeight) || canvas.clientHeight || window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pw = Math.max(1, Math.round(w * dpr));
    const ph = Math.max(1, Math.round(h * dpr));
    if (canvas.width !== pw || canvas.height !== ph) {
      canvas.width = pw;
      canvas.height = ph;
    }
  };

  const drawCover = (frame) => {
    fitCanvas();
    const cw = canvas.width;
    const ch = canvas.height;
    const fw = frame.displayWidth || frame.codedWidth;
    const fh = frame.displayHeight || frame.codedHeight;
    const scale = Math.max(cw / fw, ch / fh);
    const dw = fw * scale;
    const dh = fh * scale;
    ctx.drawImage(frame, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
  };

  const sampleReady = (sample) => received >= sample.offset + sample.size;

  const ensureDecoder = async () => {
    if (decoder || !parsed) return decoder;
    const config = {
      codec: parsed.codec,
      codedWidth: parsed.width,
      codedHeight: parsed.height,
      description: parsed.description,
      optimizeForLatency: true,
    };
    if (Decoder.isConfigSupported) {
      const support = await Decoder.isConfigSupported(config);
      if (!support || !support.supported) return null;
    }
    decoder = new Decoder({
      output(frame) {
        if (stopped) {
          frame.close();
          return;
        }
        if (pending) pending.close();
        pending = frame;
        if (!started) {
          started = true;
          if (onReady) onReady();
        }
      },
      error() {
        stopped = true;
        if (onError) onError();
      },
    });
    try {
      decoder.configure(config);
    } catch (e) {
      decoder = null;
      return null;
    }
    return decoder;
  };

  const appendChunk = (chunk) => {
    if (contentLength) {
      bytes.set(chunk, received);
      received += chunk.length;
      return;
    }
    if (received + chunk.length > bytes.length) {
      const next = new Uint8Array(Math.max(bytes.length * 2, received + chunk.length));
      next.set(bytes.subarray(0, received));
      bytes = next;
    }
    bytes.set(chunk, received);
    received += chunk.length;
  };

  const tryParse = () => {
    if (parsed || received < 24576) return;
    try {
      parsed = parseMp4Video(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + received));
    } catch (e) {
      parsed = null;
    }
  };

  const pump = () => {
    if (stopped) return;
    if (!parsed || !decoder) {
      window.requestAnimationFrame(pump);
      return;
    }
    const next = parsed.samples[index];
    if (next && !sampleReady(next)) {
      if (!stalledAt) stalledAt = performance.now();
      window.requestAnimationFrame(pump);
      return;
    }
    if (stalledAt) {
      if (origin) origin += performance.now() - stalledAt;
      stalledAt = 0;
    }
    if (!origin) origin = performance.now();
    const elapsedUs = (performance.now() - origin) * 1000;
    while (
      index < parsed.samples.length &&
      decoder.decodeQueueSize < 8 &&
      sampleReady(parsed.samples[index]) &&
      parsed.samples[index].dts <= elapsedUs + 80000
    ) {
      const sample = parsed.samples[index];
      decoder.decode(new Chunk({
        type: sample.type,
        timestamp: sample.timestamp,
        duration: sample.duration,
        data: bytes.slice(sample.offset, sample.offset + sample.size),
      }));
      index += 1;
    }
    if (
      loaded &&
      index >= parsed.samples.length &&
      decoder.decodeQueueSize === 0 &&
      !looping
    ) {
      looping = true;
      decoder.flush().then(() => {
        looping = false;
        if (stopped) return;
        index = 0;
        origin = performance.now();
      }).catch(() => {
        looping = false;
      });
    }
    window.requestAnimationFrame(pump);
  };

  const draw = () => {
    if (stopped) return;
    if (pending) {
      const frame = pending;
      pending = null;
      drawCover(frame);
      frame.close();
    }
    window.requestAnimationFrame(draw);
  };

  const stop = () => {
    stopped = true;
    if (pending) pending.close();
    pending = null;
    try { if (decoder) decoder.close(); } catch (e) { /* already closed */ }
    try { reader.cancel(); } catch (e) { /* ignore */ }
  };

  if (signal) {
    if (signal.aborted) {
      stop();
      return false;
    }
    signal.addEventListener('abort', stop, { once: true });
  }

  window.requestAnimationFrame(draw);
  window.requestAnimationFrame(pump);

  const readLoop = async () => {
    while (!stopped) {
      const result = await reader.read();
      if (result.done) {
        loaded = true;
        break;
      }
      appendChunk(result.value);
      tryParse();
      if (parsed && !decoder) {
        const ready = await ensureDecoder();
        if (!ready) {
          stop();
          return false;
        }
      }
    }
    return true;
  };

  readLoop().then((ok) => {
    if (ok === false && onError) onError();
  }).catch(() => {
    if (!stopped && onError) onError();
  });

  return true;
}
